import {
	Alert,
	Inline,
	Box,
	Container,
	Heading,
	Text,
	joinAttributes,
	Spinner,
	Stack,
	Grid,
	Col,
	Icons,
	Time,
	Dropdown,
	Divider,
	Button,
	Component,
	Tabs,
	TabsList,
	TabContent,
	RadioInput,
} from "@sembark-travel/ui/base"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { Breadcrumbs, NavLink } from "@sembark-travel/ui/router"
import { Markdown } from "@sembark-travel/ui/markdown"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { generatePath } from "../router-utils"
import {
	ITransportService,
	INormalizedService,
	TTransportServiceLocation,
	NewServiceCredentials,
} from "./store"
import { ImageMediaItem } from "./../Media"
import { useEffect, useMemo, useState } from "react"
import {
	Form,
	SubmissionError,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { TTravelActivityTicketType } from "../TravelActivities"
import { normalizeService } from "./utils"
import { isMouseEvent, useDragDrop } from "../TripPlanRequests"
import { NewServicesForm } from "./NewItem"
import { parseDate } from "@sembark-travel/datetime-utils"

export function ServiceLocationDetails({
	serviceLocationId,
	children,
}: {
	serviceLocationId: number | string
	children?: (props: {
		serviceLocation: TTransportServiceLocation
		onChange: () => void
	}) => React.ReactNode
}) {
	const xhr = useXHR()
	const { data, error, mutate } = useSWR<TTransportServiceLocation>(
		`/api/transport-service-locations/${serviceLocationId}`,
		() =>
			xhr
				.get(`/transport-service-locations/${serviceLocationId}`, {
					params: {
						include:
							"latest_media,services.service_wise_itineraries,services.latest_media,deleted_services,trip_destinations,rate_uploaded_by",
					},
				})
				.then((resp) => resp.data.data)
	)
	if (!data && error) return <Alert status="error">{error.message}</Alert>
	if (!data) return <Spinner padding="4" alignCenter />
	const {
		id,
		short_code,
		name,
		short_name,
		latest_media,
		trip_destinations,
		is_disabled,
	} = data
	return (
		<Box key={id}>
			<Breadcrumbs
				title="Service Details"
				items={[
					[generatePath("/transport-services"), "Transport Services"],
					["", short_name],
				]}
				actions={
					<Inline gap="4">
						<Component<"archive" | "restore" | undefined>
							initialState={undefined}
						>
							{({ state, setState }) => (
								<>
									<Dropdown alignRight>
										<Dropdown.ToggleButton size="sm" level="tertiary">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											{!is_disabled ? (
												<Dropdown.MenuItem onClick={() => setState("archive")}>
													<Icons.Ban /> Disable
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem onClick={() => setState("restore")}>
													<Icons.Refresh /> Restore
												</Dropdown.MenuItem>
											)}
										</Dropdown.Menu>
									</Dropdown>
									<Dialog
										open={state === "archive"}
										onClose={() => setState(undefined)}
										sm
									>
										<Dialog.Header>
											<Dialog.Title>Disable Transport Services</Dialog.Title>
										</Dialog.Header>
										<Form<{ transport_services_id: number }>
											initialValues={{ transport_services_id: id }}
											onSubmit={withServerErrors(async (values) => {
												const resp = await xhr.delete<{
													data: { message: string }
												}>(
													`transport-service-locations/${values.transport_services_id}`
												)
												mutate()
												showSnackbar(resp.data.data.message)
												setState(undefined)
											})}
											subscription={{ submitting: true }}
										>
											{({ submitting, handleSubmit }) => (
												<form noValidate onSubmit={handleSubmit}>
													<Dialog.Body>
														<Text>
															This Transport Services will be removed from all
															travel-activities listing and selection pages.
															Existing usage of this Transport Services will NOT
															be affected.
														</Text>
														<Divider sm />
														<Text color="warning">
															Are you sure you want to disable this Transport
															Services ?
														</Text>
													</Dialog.Body>
													<Dialog.Footer>
														<Button
															type="submit"
															disabled={submitting}
															status="warning"
														>
															{submitting
																? "Disabling..."
																: "I understand, disabled it."}
														</Button>
														<Button
															onClick={() => setState(undefined)}
															disabled={submitting}
														>
															Cancel
														</Button>
													</Dialog.Footer>
												</form>
											)}
										</Form>
									</Dialog>
									<Dialog
										open={state === "restore"}
										onClose={() => setState(undefined)}
										sm
									>
										<Dialog.Header>
											<Dialog.Title>
												Restore disabled Transport Services
											</Dialog.Title>
										</Dialog.Header>
										<Form<{ transport_services_id: number }>
											initialValues={{ transport_services_id: id }}
											onSubmit={withServerErrors(async (values) => {
												const resp = await xhr.patch<{
													data: { message: string }
												}>(
													`transport-service-locations/${values.transport_services_id}/restore`
												)
												mutate()
												showSnackbar(resp.data.data.message)
												setState(undefined)
											})}
											subscription={{ submitting: true }}
										>
											{({ submitting, handleSubmit }) => (
												<form noValidate onSubmit={handleSubmit}>
													<Dialog.Body>
														<Text>
															Are you sure you want to restore this disabled
															Transport Services ?
														</Text>
													</Dialog.Body>
													<Dialog.Footer>
														<Button type="submit" disabled={submitting}>
															{submitting ? "Restoring..." : "Restore"}
														</Button>
														<Button
															onClick={() => setState(undefined)}
															disabled={submitting}
														>
															Cancel
														</Button>
													</Dialog.Footer>
												</form>
											)}
										</Form>
									</Dialog>
								</>
							)}
						</Component>
					</Inline>
				}
			/>
			<Container paddingY="6" bgColor="default">
				<Inline gap="4" justifyContent="between" collapseBelow="md">
					<Stack gap="4">
						<Stack as="header" gap="1">
							<Heading as="h2">{name}</Heading>
							<Text color="muted">
								{joinAttributes(short_code, short_name)}
							</Text>
						</Stack>
						<Stack as="header" gap="1">
							<Text fontSize="sm">Trip Destinations</Text>
							<Text color="muted">
								{trip_destinations?.length
									? trip_destinations.map((t) => t.name).join(", ")
									: "All"}
							</Text>
						</Stack>
					</Stack>
					<Stack gap="4" style={{ maxWidth: "700px" }}>
						<ImageMediaItem
							media={latest_media}
							help="Image must be 1200x600 JPEG/PNG file with less than 1 MB in size."
							crop={{
								aspect: 2 / 1,
								minHeight: 100,
							}}
							canRemove
							onSubmit={async (data) => {
								await xhr.patch(
									`/transport-service-locations/${id}/image`,
									data
								)
								await mutate()
							}}
						/>
						<Text color="muted" fontSize="sm">
							<Icons.Info /> Fallback image when individual services image is
							not available.
						</Text>
					</Stack>
				</Inline>
			</Container>
			<Box borderTopWidth="1">
				<Tabs layout="col">
					<TabsList>
						<NavLink
							to={generatePath("/transport-services/:serviceLocationId", {
								serviceLocationId: String(id),
							})}
							end
						>
							Services
						</NavLink>
						<NavLink
							to={generatePath(
								"/transport-services/:serviceLocationId/activities",
								{
									serviceLocationId: String(id),
								}
							)}
						>
							Log
						</NavLink>
					</TabsList>
					<TabContent>
						{children
							? children({
									serviceLocation: data,
									onChange: mutate,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

function DeleteInDialog({
	service,
	children,
	onSuccess,
	type,
}: {
	service: INormalizedService
	children: (props: { openDisableDialog: () => void }) => React.ReactNode
	onSuccess: () => void
	type: "activity" | "transport"
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const context =
		type === "activity"
			? ["Travel", "Activity", "Ticket"]
			: ["Transport", null, "Service"]
	return (
		<>
			{children({ openDisableDialog: openDialog })}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`Disable ${context.filter(Boolean).join(" ")}`}
			>
				<Form
					initialValues={{ service: service }}
					onSubmit={withServerErrors(async ({ service }) => {
						const url =
							type === "activity"
								? `/travel-activity-tickets/${service.id}`
								: `/transport-services/${service.id}`
						await xhr.delete(url)
						showSnackbar(`${context[2]} successfully disabled.`)
						onSuccess()
						closeDialog()
					})}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<Box>
										<Heading as="h4" fontSize="md">
											Preview Details
										</Heading>
										<Inline
											gap="4"
											padding="4"
											borderWidth="1"
											rounded="md"
											flexWrap="wrap"
										>
											<Box>
												<Stack maxWidth="4xl" gap="2">
													<Inline gap="4" justifyContent="between">
														<Stack gap="1">
															<Text fontWeight="semibold" fontSize="md">
																{service.name}
															</Text>
															<Box
																fontSize="sm"
																fontWeight="semibold"
																color="muted"
															>
																{joinAttributes(
																	service.distance
																		? `${service.distance} kms`
																		: null,
																	service.start_time_formatted
																		? `Starts: ${service.start_time_formatted}`
																		: null,
																	service.duration_string,
																	service.end_time_formatted
																		? `Ends: ${service.end_time_formatted}`
																		: null
																)}
															</Box>
														</Stack>
													</Inline>
													<Box color="muted">
														<Markdown>{service.description}</Markdown>
													</Box>
												</Stack>
											</Box>
										</Inline>
									</Box>
									<Divider sm />
									<Alert
										title={`Are you sure you want to disable this ${context[2]} ? Disabling the ${context[2]}:`}
										status="warning"
									>
										<Stack as="ul" listStyleType="disc" paddingLeft="6" gap="1">
											<Box as="li">{`Will disable this ${context.filter(Boolean).join(" ").toLocaleLowerCase()}`}</Box>
											<Box as="li">
												{`Will exclude the packages, containing this disabled
												${context[2]?.toLocaleLowerCase()}, from suggession on quote creation`}
											</Box>
											<Box as="li">
												{`Will hide the ${context[2]?.toLocaleLowerCase()} from selection at the time of
												package creation`}
											</Box>
										</Stack>
									</Alert>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button
											disabled={submitting}
											type="submit"
											status="warning"
										>
											{submitting
												? "Disabling..."
												: "I understand, disable it."}
										</Button>
										<Button disabled={submitting} onClick={closeDialog}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

interface TServicesDetailsProps<T> {
	type: "activity" | "transport"
	services: T[]
}

export function ServicesDetails({
	services,
	mutate,
	isDisable,
	type,
	heading,
}: TServicesDetailsProps<TTravelActivityTicketType | ITransportService> & {
	mutate: () => void
	isDisable: boolean
	heading: string
}) {
	const [areDeletedVisible, toggleDeletedVisibility] = useState(!!isDisable)
	const [nonDeletedServices, deletedServices] = (services || []).reduce<
		[
			Array<ITransportService | TTravelActivityTicketType>,
			Array<ITransportService | TTravelActivityTicketType>,
		]
	>(
		([nonDeletedServices, deletedServices], s) => {
			if (s.deleted_at) {
				deletedServices.push(s)
			} else {
				nonDeletedServices.push(s)
			}
			return [nonDeletedServices, deletedServices]
		},
		[[], []]
	)
	const deletedServicesCount = deletedServices.length
	const enableServicesCount = nonDeletedServices.length
	useEffect(() => {
		if (deletedServicesCount === 0 && enableServicesCount === 0) return
		if (deletedServicesCount === 0 && areDeletedVisible) {
			toggleDeletedVisibility(false)
		}
		if (enableServicesCount === 0 && !areDeletedVisible) {
			toggleDeletedVisibility(true)
		}
	}, [deletedServicesCount, areDeletedVisible, enableServicesCount])

	return (
		<Container bgColor={"inset"}>
			<Stack paddingY="8" gap="4">
				<Inline justifyContent="between" gap="2">
					<Heading as="h4">{heading}</Heading>
					<Inline alignItems={"center"} gap={"2"}>
						{nonDeletedServices.length > 1 &&
						!areDeletedVisible &&
						isMouseEvent ? (
							<ServicesRearrangementDialogBox
								editableData={nonDeletedServices}
								onSucces={mutate}
								type={type}
							>
								{({ open }: { open: () => void }) => (
									<Button onClick={open}>
										Re-arrange {type === "activity" ? "Tickets" : "Services"}{" "}
										<Icons.NewFeature width="10" height="6" />
									</Button>
								)}
							</ServicesRearrangementDialogBox>
						) : null}
						{enableServicesCount && deletedServicesCount ? (
							<Inline
								gap="4"
								as="label"
								paddingY="2"
								paddingX="4"
								bgColor={{ hover: "subtle" }}
							>
								<RadioInput
									checked={false}
									name={`showEnable`}
									value={"0"}
									onChange={() => toggleDeletedVisibility(!areDeletedVisible)}
								/>
								{!areDeletedVisible
									? `Only Disabled (${deletedServicesCount})`
									: `Only Enable (${enableServicesCount})`}
							</Inline>
						) : null}
					</Inline>
				</Inline>
				<Stack as="ul" gap="4">
					{(areDeletedVisible ? deletedServices : nonDeletedServices).map(
						(s) => {
							return (
								<Box
									as="li"
									key={s.id}
									borderWidth="1"
									bgColor={s.deleted_at ? "warning" : "default"}
									borderColor={s.deleted_at ? "warning" : "default"}
									padding="4"
									rounded="md"
								>
									<SingleService
										service={s as TTravelActivityTicketType | ITransportService}
										mutate={mutate}
										type={type}
										isDrag={false}
										index={undefined}
									/>
								</Box>
							)
						}
					)}
				</Stack>
			</Stack>
		</Container>
	)
}

function SingleService({
	service,
	mutate,
	type,
	isDrag,
	index,
}: {
	service: TTravelActivityTicketType | ITransportService
	mutate: (() => void) | undefined
	type: "activity" | "transport"
	isDrag: boolean
	index: number | undefined
}) {
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()
	const mapped = useMemo(() => normalizeService(service, type), [service, type])
	return (
		<Grid style={{ cursor: isDrag ? "grab" : "pointer" }} gap="4">
			<Col>
				<Stack gap="4">
					<Stack maxWidth="4xl" gap="2">
						<Inline paddingX={"2"} gap={"4"} justifyContent="between">
							<Inline gap="4" alignItems="center">
								{isDrag ? (
									<Text fontWeight={"normal"}>{index ? index + 1 : 1}.</Text>
								) : null}
								<Stack color={isDrag ? "default" : undefined} gap="1">
									{isDrag ? (
										<Text fontWeight={"normal"}>
											{mapped.name}{" "}
											{mapped.short_name ? (
												<Text as="span" color="muted">
													[[{mapped.short_name}]]
												</Text>
											) : null}
										</Text>
									) : (
										<Heading as="h5" fontWeight={"semibold"} fontSize={"md"}>
											{mapped.name}{" "}
											{mapped.short_name ? (
												<Text as="span" color="muted">
													[[{mapped.short_name}]]
												</Text>
											) : null}
										</Heading>
									)}

									<Box fontSize="sm" fontWeight="semibold" color="muted">
										{joinAttributes(
											mapped.distance ? `${mapped.distance} kms` : null,
											mapped.start_time_formatted
												? `Starts: ${mapped.start_time_formatted}`
												: null,
											mapped.duration_string || null,
											mapped.end_time_formatted
												? `Ends: ${mapped.end_time_formatted}`
												: null
										)}
									</Box>
									{mapped.closed_on_days_or_dates?.length && !isDrag ? (
										<Text
											color="warning"
											fontSize="sm"
											fontWeight="semibold"
											title="Closed on Days of Week or Dates"
										>
											<Icons.Ban /> :{" "}
											{mapped.closed_on_days_or_dates.join(", ")}
										</Text>
									) : null}
								</Stack>
							</Inline>
							{isDrag ? (
								<Stack justifyContent={"center"}>
									<Box
										padding={"1"}
										bgColor={"accent"}
										color={"primary"}
										borderWidth={"1"}
										borderColor={"accent"}
										rounded="md"
									>
										<Icons.GripVertical />
									</Box>
								</Stack>
							) : null}
							{hasPermission(PERMISSIONS.UPLOAD_BULK_TRANSPORT_SERVICES) &&
							!isDrag &&
							mutate ? (
								<Box>
									<DeleteInDialog
										type={type}
										service={mapped}
										onSuccess={mutate}
									>
										{({ openDisableDialog }) => (
											<Dropdown alignRight>
												<Dropdown.ToggleButton size="sm" level="tertiary">
													<Icons.DotsVertical />
												</Dropdown.ToggleButton>
												<Dropdown.Menu>
													{type === "transport" && !service.deleted_at ? (
														<ServiceEdit
															onSucces={mutate}
															serviceLocationId={
																(service as ITransportService).locations.id
															}
															serviceId={service.id}
														>
															{({ open }) => (
																<Dropdown.MenuItem onClick={open}>
																	<Icons.Pencil /> Edit Service{" "}
																	<Icons.NewFeature size={"10"} />
																</Dropdown.MenuItem>
															)}
														</ServiceEdit>
													) : null}
													{!service.deleted_at ? (
														<Dropdown.MenuItem onClick={openDisableDialog}>
															<Icons.Ban color="warning" /> Disable{" "}
															{type === "transport" ? "Service" : "Ticket"}
														</Dropdown.MenuItem>
													) : (
														<Dropdown.MenuItem
															onClick={async () => {
																if (
																	window.confirm(
																		`Are you sure you want to enable/restore this ${type === "activity" ? "ticket" : "service"} ?`
																	)
																) {
																	const url =
																		type === "activity"
																			? `/travel-activity-tickets/${service.id}/restore`
																			: `/transport-services/${service.id}/restore`
																	await xhr.patch(url)
																	showSnackbar(
																		`${type === "activity" ? "Ticket" : "Service"} restored successfully.`
																	)
																	mutate()
																}
															}}
														>
															<Icons.Refresh color="success" /> Restore
														</Dropdown.MenuItem>
													)}
												</Dropdown.Menu>
											</Dropdown>
										)}
									</DeleteInDialog>
								</Box>
							) : null}
						</Inline>
						{mapped?.description && !isDrag ? (
							<Box color="muted">
								<Markdown>{mapped.description}</Markdown>
							</Box>
						) : null}
					</Stack>
					{isDrag ? null : (
						<Inline gap={{ xs: "1", sm: "6" }} collapseBelow="sm">
							<Inline gap="2" alignItems="baseline">
								<Box>
									<Icons.User />
								</Box>

								{joinAttributes(
									<Text fontSize="sm">
										Created by {mapped.created_by?.name} on{" "}
										<Time timestamp={mapped.created_at} />
									</Text>,
									mapped.updated_by ? (
										<Text fontSize="sm">
											Last Updated by {mapped.updated_by?.name} on{" "}
											<Time timestamp={mapped.updated_at} />
										</Text>
									) : null,
									mapped.rate_uploaded_by ? (
										<Text fontSize="sm">
											Rates Updated by {mapped.rate_uploaded_by?.name}
										</Text>
									) : null,
									mapped.rate_expiry_date ? (
										<Text fontSize="sm">
											Rate Validity on{" "}
											<Time
												localTimestamp={
													mapped?.rate_expiry_date_local ?? undefined
												}
												timestamp={mapped.rate_expiry_date}
											/>
										</Text>
									) : null
								)}
							</Inline>
							{mapped.deleted_at ? (
								<Inline gap="2" alignItems="baseline">
									<Box>
										<Icons.Ban />
									</Box>
									<Text fontSize="sm">
										Disabled by {mapped.deleted_by?.name} on{" "}
										<Time timestamp={mapped.deleted_at} />
									</Text>
								</Inline>
							) : null}
						</Inline>
					)}
				</Stack>
			</Col>
			{isDrag || !mutate ? null : (
				<Col xs={12} sm={4} lg={3}>
					<MediaPlaceholder type={type} service={mapped} onSuccess={mutate} />
				</Col>
			)}
		</Grid>
	)
}

function MediaPlaceholder({
	type,
	service,
	onSuccess,
}: {
	type: "activity" | "transport"
	service: INormalizedService
	onSuccess: () => void
}) {
	const xhr = useXHR()

	return (
		<ImageMediaItem
			media={service.latest_media}
			help="Image must be 1200x600 JPEG/PNG file with less than 1 MB in size."
			crop={{
				aspect: 2 / 1,
				minHeight: 100,
			}}
			canRemove
			onSubmit={async (data) => {
				const url =
					type === "activity"
						? `/travel-activity-ticket-types/${service.id}/image`
						: `/transport-services/${service.id}/image`
				await xhr.patch(url, data)
				await onSuccess()
			}}
		/>
	)
}

export function ServicesRearrangementDialogBox({
	children,
	editableData,
	onSucces,
	type,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	editableData: Array<TTravelActivityTicketType | ITransportService>
	onSucces: () => void
	type: "activity" | "transport"
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open: () => open() })}
			<Dialog
				open={isOpen}
				onClose={close}
				title={
					type === "activity"
						? "Tickets Re-arrangement"
						: "Services Re-arrangement"
				}
			>
				<Dialog.Body>
					<ServicesRearrangement
						refresh={onSucces}
						editableData={editableData}
						onCancel={close}
						type={type}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function ServicesRearrangement({
	onCancel,
	refresh,
	editableData,
	type,
}: {
	onCancel: () => void
	refresh: () => void
	editableData: Array<TTravelActivityTicketType | ITransportService>
	type: "activity" | "transport"
}) {
	const [data, setData] = useState(editableData)
	const [isReordered, setIsReordered] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const {
		handleDragStart,
		handleDragOver,
		handleDrop,
		getRowStyle,
		handleDragLeave,
	} = useDragDrop<TTravelActivityTicketType | ITransportService>(
		data,
		(ids) => {
			setData(ids)
			if (!isReordered) {
				setIsReordered(true)
			}
		}
	)

	const xhr = useXHR()

	async function handelSubmit() {
		try {
			setIsLoading(true)
			const priorityData = data.map((s) => s.id)
			let id: number | undefined,
				url = ""

			if (type === "activity" && "activity_id" in data[0]) {
				id = data[0].activity_id
				url = `/travel-activities/${id}/reorder-ticket-types`
			} else if (type === "transport" && "location_id" in data[0]) {
				id = data[0].location_id
				url = `transport-service-locations/${id}/reorder-services`
			}
			if (!id || !url) {
				throw new Error("No valid id found for ticket reordering.")
			}
			const key =
				type === "activity" ? "ordered_ticket_type_ids" : "ordered_service_ids"
			await xhr.post(url, {
				[key]: priorityData,
			})
			refresh()
			if (onCancel) {
				onCancel()
			}
		} catch (err) {
			const error = err as Error
			window.alert(
				error?.message ||
					"Sorry! Something went wrong. Please try again after some time."
			)
			setData(editableData)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Stack as="ul" gap="4">
			<Stack gap="2">
				{data.map((s, index) => {
					return (
						<Stack
							key={s.id}
							draggable
							onDragStart={(e) => handleDragStart(e, index)}
							onDragOver={(e) => handleDragOver(e, index)}
							onDrop={(e) => handleDrop(e, index)}
							onDragLeave={handleDragLeave}
							style={getRowStyle(index)}
							padding={"1"}
							bgColor={"default"}
							borderColor={"default"}
							as="li"
							borderWidth="1"
							rounded="md"
							paddingY={"2"}
						>
							<SingleService
								isDrag={true}
								service={s}
								mutate={undefined}
								type={type}
								index={index}
							/>
						</Stack>
					)
				})}
			</Stack>
			<Divider sm />
			<Inline justifyContent={"end"} gap={"3"}>
				<Button onClick={onCancel}>Cancel</Button>
				<Button
					level="primary"
					disabled={isLoading || !isReordered}
					status="primary"
					onClick={handelSubmit}
					type="submit"
				>
					{isLoading ? "Submitting..." : "Submit"}
				</Button>
			</Inline>
		</Stack>
	)
}

function ServiceEdit({
	children,
	onSucces,
	serviceLocationId,
	serviceId,
}: {
	children: (props: { open: () => void }) => React.ReactNode
	onSucces: () => void
	serviceLocationId: number
	serviceId: number
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open: () => open() })}
			<Dialog xl open={isOpen} onClose={close} title={"Edit Transport Service"}>
				<Dialog.Body>
					<ServiceEditForm
						serviceLocationId={serviceLocationId}
						serviceId={serviceId}
						onSucces={onSucces}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
function ServiceEditForm({
	onSucces,
	onCancel,
	serviceLocationId,
	serviceId,
}: {
	onSucces: () => void
	onCancel: () => void
	serviceLocationId: number
	serviceId: number
}) {
	const xhr = useXHR()
	const { data, error } = useSWR<TTransportServiceLocation>(
		`/api/transport-service-locations/${serviceLocationId}/services/${serviceId}`,
		() =>
			xhr
				.get(`/transport-service-locations/${serviceLocationId}`, {
					params: {
						service_id: serviceId,
						include:
							"services.service_wise_itineraries,services,trip_destinations",
					},
				})
				.then((resp) => resp.data.data)
	)

	const initialValues = useMemo<NewServiceCredentials>(() => {
		const dataService = data?.services?.[0]

		return {
			from: data?.from as NewServiceCredentials["from"] | undefined,
			to: data?.to as NewServiceCredentials["to"] | undefined,
			trip_destinations: data?.trip_destinations,
			quick_add: false,
			transport_service: [
				{
					service: dataService?.service_display_name ?? "",
					service_short_name: dataService?.service_short_name ?? "",
					distance: dataService?.distance,
					start_time: dataService?.start_time
						? parseDate(dataService.start_time, "HH:mm")
						: undefined,
					duration: dataService?.duration,
					service_details:
						dataService?.service_wise_itineraries?.[0]?.description ?? "",
					short_code: data?.short_code,
				},
			],
		}
	}, [data])

	if (!data && error) return <Alert status="error">{error.message}</Alert>
	if (!data) return <Spinner padding="4" alignCenter />
	return (
		<NewServicesForm
			onSuccess={() => {
				onCancel()
				onSucces()
			}}
			onCancel={onCancel}
			initialValues={initialValues}
			type={"EditSingleService"}
			serviceLocationId={serviceLocationId}
			canAddMoreServices={false}
			url={`/transport-services/${serviceId}`}
		/>
	)
}
